"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  detectMovieRegion,
  fetchMovieProviders,
  type MediaType,
  type MovieProvider,
  type MovieProvidersResult,
} from "@/lib/api";
import { countryName } from "@/lib/countries";
import { VivaCineRegionPicker } from "@/components/viva-cine-region-picker";
import { saveRegion } from "@/lib/detect-region";
import { useCountryStore } from "@/lib/country-store";
import { MovieDataAttribution } from "@/components/movie-data-attribution";
import { providerLinkLabel } from "@/lib/movies";
import { useLocale, useMessages } from "@/lib/i18n/client";
import messages from "./movie-providers.i18n";
import { track } from "@/lib/tracking/track";

function dedupProviders(a: MovieProvider[], b: MovieProvider[]): MovieProvider[] {
  const seen = new Set<string>();
  return [...a, ...b].filter((p) => {
    if (seen.has(p.providerName)) return false;
    seen.add(p.providerName);
    return true;
  });
}

function ProviderChip({ provider, movieId }: { provider: MovieProvider; movieId: number }) {
  const lang = useLocale();
  const content = (
    <>
      <Image
        src={provider.logoUrl}
        alt={provider.providerName}
        width={24}
        height={24}
        className="rounded"
      />
      <span className="text-sm font-medium">{provider.providerName}</span>
    </>
  );

  // Se ve como botón, así que si hay link tiene que comportarse como uno
  // (Clarity mostraba clics "muertos" en estos chips).
  if (!provider.url) {
    return (
      <div className="flex items-center gap-2 rounded-full border border-border bg-surface px-3 py-1.5">
        {content}
      </div>
    );
  }

  return (
    <a
      href={provider.url}
      target="_blank"
      rel="noopener noreferrer"
      title={providerLinkLabel(provider, lang)}
      onClick={() => track("movie_provider_click", { tmdb_id: movieId, provider: provider.providerName, featured: false })}
      className="flex items-center gap-2 rounded-full border border-border bg-surface px-3 py-1.5 transition hover:cursor-pointer hover:border-primary/50"
    >
      {content}
      <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
    </a>
  );
}

interface MovieProvidersProps {
  movieId: number;
  mediaType?: MediaType;
  // Región + datos ya resueltos server-side (cacheados 24h) — se pintan de
  // inmediato, sin spinner, y sirven de contenido indexable para Google.
  initialRegion: string;
  initialProviders: MovieProvidersResult | null;
}

export function MovieProviders({
  movieId,
  mediaType = "movie",
  initialRegion,
  initialProviders,
}: MovieProvidersProps) {
  const [region, setRegion] = useState(initialRegion);
  const [providers, setProviders] = useState<MovieProvidersResult | null>(initialProviders);
  const [loading, setLoading] = useState(!initialProviders);
  const t = useMessages(messages);
  const lang = useLocale();

  // El HTML cacheado trae CL; en el cliente se pasa al país que el usuario
  // eligió antes en el selector o, si nunca eligió, al deducido en el
  // dispositivo (sin caché) apenas se sabe cuál es.
  // También al cambiar el país en el selector global (header): Viva Cine
  // vuelve a seguirlo.
  const globalCountry = useCountryStore((s) => s.country);
  useEffect(() => {
    detectMovieRegion().then((detected) => {
      if (detected.countryCode !== region) {
        setRegion(detected.countryCode);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCountry]);

  function selectRegion(next: string) {
    saveRegion(next);
    setRegion(next);
  }

  useEffect(() => {
    if (region === initialRegion && providers) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    fetchMovieProviders(movieId, region, { mediaType, lang })
      .then(setProviders)
      .catch(() => toast.error(t.loadError))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movieId, mediaType, region, lang]);

  const rentOrBuy = providers ? dedupProviders(providers.rent, providers.buy) : [];

  return (
    <div>
      <VivaCineRegionPicker
        label={t.showingFor(countryName(region, lang))}
        value={region}
        onChange={selectRegion}
      />

      <div className="mt-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t.searching}
          </div>
        ) : providers && !providers.hasAnyProviders ? (
          <p className="text-sm text-muted-foreground">
            {t.notFound(mediaType === "tv", countryName(region, lang))}
          </p>
        ) : providers ? (
          <div className="space-y-5">
            {providers.flatrate.length > 0 && (
              <div>
                <h3 className="mb-2 text-sm font-medium text-muted-foreground">{t.streaming}</h3>
                <div className="flex flex-wrap gap-2">
                  {providers.flatrate.map((p) => (
                    <ProviderChip key={p.providerName} provider={p} movieId={movieId} />
                  ))}
                </div>
              </div>
            )}
            {rentOrBuy.length > 0 && (
              <div>
                <h3 className="mb-2 text-sm font-medium text-muted-foreground">{t.rentOrBuy}</h3>
                <div className="flex flex-wrap gap-2">
                  {rentOrBuy.map((p) => (
                    <ProviderChip key={p.providerName} provider={p} movieId={movieId} />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Siempre visible (también sin resultados o cargando): los datos de
          la página entera vienen de TMDB/JustWatch, no solo los providers. */}
      <p className="mt-5 text-xs text-muted-foreground">
        <MovieDataAttribution />
      </p>
    </div>
  );
}
